<template>
  <div>
    <PFTextField
      ref="textField"
      v-model.trim="phoneNumberBodyMasked"
      type="text"
      inputmode="tel"
      :error="error"
      :force-placeholder-active="isMasked"
      v-bind="{ ...$attrs, ...$props }"
      @keypress="onlyPhoneNumber"
      v-on="inputListeners"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { parsePhoneNumber } from 'libphonenumber-js/min'
import InputMask from 'inputmask'
import PFTextField from '@/components/OldUI/newUI/PFTextField'

const PATTERNS = {
  UA: '+380 (99) 999-99-99',
  RU: '+7 (999) 999-99-99',
  KZ: '+7 (799) 999-99-99',
  BY: '+375 (99) 999-99-99'
}

const shortcutsMap = {
  UA: ['+380', '380', '0'],
  // Item could be arrayed, if array provided - [search shortcut, replace shortcut]
  RU: ['+7', '7', ['89', '8'], ['90', ''], ['91', ''], ['92', ''], ['93', ''], ['94', ''], ['95', ''], ['96', ''], ['97', ''], ['98', '']],
  KZ: ['+77', '77', '+76', '76', '87', ['86', '8']],
  BY: ['+375', '375', '80']
}

export default {
  name: 'PhoneField',
  components: {
    PFTextField
  },
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Введите номер телефона'
    }
  },
  data: () => ({
    phoneNumberBodyMasked: '',
    currentCountryCode: null,

    loadCountriesPromise: null,
    isMasked: false
  }),
  computed: {
    ...mapState({
      countryCodes: state => state.users.countryCodes,
      countryCodesLoading: state => state.users.countryCodesLoading
    }),
    inputListeners () {
      return {
        ...this.$listeners,
        input: this.handleInput,
        paste: this.handlePaste
      }
    },
    phoneNumberBody () {
      if (PATTERNS[this.currentCountry?.code]) {
        return InputMask.unmask(this.phoneNumberBodyMasked, {
          mask: PATTERNS[this.currentCountry.code]
        })
      }

      return this.phoneNumberBodyMasked
    },
    phoneNumberFull () {
      if (PATTERNS[this.currentCountry?.code]) {
        return (this.currentCountry?.dial_code || '') + this.phoneNumberBody
      }

      return this.phoneNumberBody
    },
    currentCountry () {
      return this.countryCodes.find(country => country.code === this.currentCountryCode)
    }
  },
  watch: {
    value (value) {
      if (!this.isMasked) {
        this.isMasked = true
      }

      if (value !== this.phoneNumberFull) {
        this.setPhone(value)
      }
    }
  },
  async created () {
    await this.loadCountries()

    await this.setPhone(this.value)
  },
  methods: {
    focus () { // ref method
      this.$refs.textField.focus()
    },
    async setPhone (phoneNumber) {
      await this.loadCountriesPromise

      const parsedPhoneNumber = this.tryParsePhoneNumber(phoneNumber)

      this.currentCountryCode = parsedPhoneNumber?.country
      this.replaceMask(this.currentCountryCode)
      this.phoneNumberBodyMasked = phoneNumber
    },
    tryParsePhoneNumber (phoneNumber) {
      let parsedPhoneNumber
      try {
        parsedPhoneNumber = parsePhoneNumber(phoneNumber)
      } catch (e) { }

      return parsedPhoneNumber
    },
    async loadCountries () {
      if (this.$store.state.users.countryCodes.length === 0) {
        await (this.loadCountriesPromise = this.$store.dispatch('users/fetchCountryCodes'))
      } else {
        this.loadCountriesPromise = Promise.resolve()
      }
    },
    replaceMask (countryCode) {
      if (process.server) {
        return
      }
      const inputEl = this.$refs.textField.$el.querySelector('input')

      const mask = PATTERNS[countryCode]

      if (mask) {
        const oldPhoneBody = this.phoneNumberBody
        InputMask({
          mask
        }).mask(inputEl)

        this.phoneNumberBodyMasked = oldPhoneBody
      } else {
        inputEl.inputmask?.remove()
      }
    },
    handleInput () {
      this.predictCountry()
      this.$emit('input', this.phoneNumberFull)
    },
    handlePaste (e) {
      e.preventDefault()
      const clipboardData = e.clipboardData || window.clipboardData
      const pastedText = clipboardData.getData('Text')

      if (this.tryParsePhoneNumber(pastedText)) {
        this.setPhone(pastedText)
        this.$nextTick(() => this.$emit('input', this.phoneNumberFull))
        return
      }

      setTimeout(() => {
        const onlyDigits = pastedText.replace(/[^\d.]/g, '')
        this.phoneNumberBodyMasked = onlyDigits
        this.handleInput()
      }, 0)
    },
    async predictCountry () {
      if (this.phoneNumberBody.length === 0) {
        this.$nextTick(() => {
          this.currentCountryCode = null
          this.replaceMask()
        })
        return
      }

      if (this.currentCountryCode) {
        return
      }

      for (const [countryCode, shortcuts] of Object.entries(shortcutsMap)) {
        const shortcutTuple = shortcuts.find((shortcut) => {
          const searchShortcut = Array.isArray(shortcut) ? shortcut[0] : shortcut

          return this.phoneNumberBody.startsWith(searchShortcut)
        })

        if (shortcutTuple) {
          const replaceShortcut = Array.isArray(shortcutTuple) ? shortcutTuple[1] : shortcutTuple

          this.phoneNumberBodyMasked = this.phoneNumberBodyMasked.replace(replaceShortcut, '')
          this.currentCountryCode = countryCode
          await this.$nextTick()
          this.replaceMask(countryCode)
          break
        }
      }
    },
    onlyPhoneNumber (e) {
      const charCode = e.keyCode
      if (charCode !== 43 && charCode > 31 && (charCode < 48 || charCode > 57)) {
        e.preventDefault()
      } else {
        return true
      }
    }
  }
}
</script>

<style module lang="scss">
@import 'assets/scss/shared';

.phone {
  display: flex;
  position: relative;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #d0d0d0;
  box-sizing: border-box;

  &:hover {
    border-color: #fecdb4;
  }
}

.phoneFocus {
  border: 2px solid #fecdb4;
}

.phoneError {
  border-color: #fe675e !important;
}

.phoneInput {
  border: none;
  appearance: none;
  height: 48px;
  width: 100%;
  padding: 14px 16px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 8px;
  color: $typography-paragraph;
  font-weight: 400;
  z-index: 1;
  position: relative;

  &::placeholder {
    color: $typography-placeholder;
    font-weight: 400;
    font-size: 14px;
  }
}
</style>
